import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clock,
  XCircle,
  User,
  CreditCard,
  Car,
  Home,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import type { MarkPassUsedRequest } from '@condo/contracts';
import { MarkPassUsedRequestSchema } from '@condo/contracts';
import { useVerifyPass, useMarkPassUsed } from '@/features/visitas/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

function formatDt(iso: string) {
  return format(new Date(iso), "d MMM yyyy, HH:mm", { locale: es });
}

function BrandedHeader() {
  return (
    <header className="flex items-center justify-center gap-2 py-6">
      <div className="grid h-9 w-9 place-items-center rounded-sm bg-[var(--color-primary)] text-white shadow-sm">
        <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
          <path d="M6 24V12l10-6 10 6v12h-6v-8h-8v8H6z" fill="currentColor" />
        </svg>
      </div>
      <span className="font-display text-xl font-semibold tracking-tight text-[var(--color-ink)]">
        Condo
      </span>
    </header>
  );
}

function PinForm({ token }: { token: string }) {
  const mutation = useMarkPassUsed(token);
  const form = useForm<MarkPassUsedRequest>({
    resolver: zodResolver(MarkPassUsedRequestSchema),
    defaultValues: { pin: '', tipo: 'familiar' },
  });

  async function onSubmit(values: MarkPassUsedRequest) {
    try {
      await mutation.mutateAsync(values);
      toast.success('Ingreso registrado');
    } catch {
      toast.error('PIN incorrecto o pase ya utilizado');
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
      <div>
        <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
          Tipo de visita (opcional)
        </label>
        <select
          {...form.register('tipo')}
          className="w-full rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)]"
        >
          <option value="familiar">Familiar</option>
          <option value="delivery">Delivery</option>
          <option value="proveedor">Proveedor</option>
          <option value="servicio">Servicio</option>
        </select>
      </div>
      <div>
        <Label htmlFor="pin" className="text-center block">
          PIN de verificacion (4 digitos)
        </Label>
        <Input
          id="pin"
          type="text"
          inputMode="numeric"
          maxLength={4}
          placeholder="0000"
          autoComplete="one-time-code"
          className="mt-2 text-center text-2xl font-mono tracking-[0.3em]"
          {...form.register('pin')}
        />
        {form.formState.errors.pin && (
          <p className="mt-1 text-center text-xs text-[var(--color-danger)]">
            {form.formState.errors.pin.message}
          </p>
        )}
      </div>
      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Verificando...
          </>
        ) : (
          <>
            <ShieldCheck className="h-4 w-4" />
            Marcar como ingresado
          </>
        )}
      </Button>
      <p className="text-xs text-[var(--color-ink-muted)] mt-2">
        Al confirmar, se registra el ingreso de la visita en el libro del condominio.
      </p>
    </form>
  );
}

export function VerificarRoute() {
  const { token } = useParams<{ token: string }>();
  const { data: pass, isLoading, isError } = useVerifyPass(token);

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-paper-dim)]">
      <BrandedHeader />

      <main className="flex flex-1 items-start justify-center px-4 pb-12">
        <div className="w-full max-w-sm">
          {/* Card */}
          <div className="rounded-lg border border-[var(--color-hairline)] bg-[var(--color-paper)] p-6 shadow-sm">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="mx-auto h-12 w-12 rounded-full" />
                <Skeleton className="h-4 w-3/4 mx-auto" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : isError || !pass ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--color-danger-50)]">
                  <XCircle className="h-7 w-7 text-[var(--color-danger)]" />
                </div>
                <h2 className="font-display text-lg font-semibold text-[var(--color-ink)]">
                  Pase no encontrado
                </h2>
                <p className="text-sm text-[var(--color-ink-muted)]">
                  El enlace no es valido o el pase fue eliminado.
                </p>
              </div>
            ) : pass.status === 'expired' ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--color-paper-dim)]">
                  <Clock className="h-7 w-7 text-[var(--color-ink-muted)]" />
                </div>
                <h2 className="font-display text-lg font-semibold text-[var(--color-ink)]">
                  Pase expirado
                </h2>
                <p className="text-sm text-[var(--color-ink-muted)]">
                  Este pase de visita ya no es valido. Solicita uno nuevo al residente.
                </p>
                <p className="text-xs text-[var(--color-ink-faint)]">
                  Expiro: {formatDt(pass.expiresAt)}
                </p>
              </div>
            ) : pass.status === 'used' ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--color-moss-50)]">
                  <CheckCircle2 className="h-7 w-7 text-[var(--color-moss)]" />
                </div>
                <h2 className="font-display text-lg font-semibold text-[var(--color-ink)]">
                  Ya ingresado
                </h2>
                <p className="text-sm text-[var(--color-ink-muted)]">
                  Este pase ya fue utilizado para registrar el ingreso.
                </p>
                {pass.usedAt && (
                  <p className="text-xs text-[var(--color-ink-faint)]">
                    Ingresado: {formatDt(pass.usedAt)}
                  </p>
                )}
              </div>
            ) : (
              /* pending */
              <>
                <div className="flex flex-col items-center gap-1 text-center">
                  <h2 className="font-display text-lg font-semibold text-[var(--color-ink)]">
                    Pase de visita
                  </h2>
                  <p className="text-sm text-[var(--color-ink-muted)]">
                    Verificar identidad del visitante
                  </p>
                </div>

                <div className="mt-5 space-y-2.5">
                  <div className="flex items-center gap-3 text-sm">
                    <User className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
                    <span className="font-medium">{pass.visitorNombre}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CreditCard className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
                    <span>{pass.visitorDni}</span>
                  </div>
                  {pass.vehiclePatent && (
                    <div className="flex items-center gap-3 text-sm">
                      <Car className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
                      <span>{pass.vehiclePatent}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm">
                    <Home className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
                    <span>Unidad {pass.unidadNumero}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
                    <span className="text-[var(--color-ink-muted)]">
                      Autorizado por: {pass.createdByNombre}
                    </span>
                  </div>
                </div>

                <div className="my-5 h-px bg-[var(--color-hairline)]" />

                <PinForm token={token!} />
              </>
            )}
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-[0.6875rem] text-[var(--color-ink-faint)]">
            Sistema de control de acceso condominial
          </p>
        </div>
      </main>
    </div>
  );
}
